import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Button } from 'reactstrap';
import ConfirmDialog from './ConfirmDialog';

// Single entry point for the button hierarchy (see BUTTONS.md).
//
//   primary      solid, the one main action of a view
//   secondary    neutral, bordered
//   tertiary     text only
//   destructive  quiet trigger; always opens a confirm dialog before onClick
//   icon         icon-only, `label` is required (aria-label + tooltip)
const VARIANT_CLASS = {
  primary: { color: 'primary' },
  secondary: { color: 'light' },
  tertiary: { color: 'link', className: 'btn-tertiary text-decoration-none' },
  destructive: { color: 'danger', outline: true },
  icon: { color: 'ghost-secondary', className: 'btn-icon hy-icon-action' },
};

const DEFAULT_CONFIRM = {
  title: 'Delete this record?',
  message: 'It will be permanently deleted. This cannot be undone.',
  confirmLabel: 'Yes, delete it',
};

const ActionButton = ({
  variant = 'secondary',
  icon,
  label,
  children,
  onClick,
  confirm,
  loading = false,
  disabled = false,
  danger = false,
  className = '',
  ...rest
}) => {
  const [confirming, setConfirming] = useState(false);
  const isIcon = variant === 'icon';
  // `confirm={false}` opts out where the caller already shows its own dialog;
  // passing a `confirm` config opts any variant in.
  const needsConfirm =
    confirm !== false &&
    (variant === 'destructive' ||
      (isIcon && danger) ||
      (confirm !== null && typeof confirm === 'object'));
  const config = VARIANT_CLASS[variant] || VARIANT_CLASS.secondary;

  if (isIcon && !label) {
    // An icon-only button with no accessible name is unusable by screen readers.
    // eslint-disable-next-line no-console
    console.warn('ActionButton: variant="icon" requires a `label` prop.');
  }

  const color = isIcon && danger ? 'ghost-danger' : config.color;
  const iconNode = icon && (
    <i
      className={`${icon} align-middle${isIcon ? '' : ' me-2'}`}
      aria-hidden='true'
    />
  );

  // An async onClick puts the button in its own loading state and drops any
  // click that arrives before it settles, so callers do not need a flag of
  // their own to prevent duplicate submissions. The `loading` prop still works
  // for callers that track the state themselves.
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const busy = loading || pending;

  const handleClick = e => {
    if (pendingRef.current) return;
    if (needsConfirm) {
      setConfirming(true);
      return;
    }
    const result = onClick?.(e);
    if (typeof result?.then === 'function') {
      pendingRef.current = true;
      setPending(true);
      const settle = () => {
        pendingRef.current = false;
        setPending(false);
      };
      result.then(settle, settle);
    }
  };

  const handleConfirm = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    try {
      await onClick?.();
    } finally {
      pendingRef.current = false;
      setPending(false);
      setConfirming(false);
    }
  };

  const confirmProps = { ...DEFAULT_CONFIRM, ...confirm };

  return (
    <>
      <Button
        type='button'
        color={color}
        outline={config.outline && !(isIcon && danger)}
        className={[config.className, className].filter(Boolean).join(' ')}
        disabled={disabled || busy}
        onClick={handleClick}
        aria-label={isIcon ? label : undefined}
        title={isIcon ? label : undefined}
        aria-busy={busy || undefined}
        {...rest}
      >
        {busy ? (
          <i
            className={`ri-loader-4-line ri-spin align-middle${isIcon ? '' : ' me-2'}`}
            aria-hidden='true'
          />
        ) : (
          iconNode
        )}
        {!isIcon && (children ?? label)}
      </Button>
      {needsConfirm && (
        <ConfirmDialog
          isOpen={confirming}
          tone='danger'
          title={confirmProps.title}
          message={confirmProps.message}
          subject={confirmProps.subject}
          confirmLabel={confirmProps.confirmLabel}
          cancelLabel={confirmProps.cancelLabel}
          onConfirm={handleConfirm}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
};

ActionButton.propTypes = {
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'tertiary',
    'destructive',
    'icon',
  ]),
  icon: PropTypes.string,
  label: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  confirm: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      title: PropTypes.string,
      message: PropTypes.node,
      subject: PropTypes.node,
      confirmLabel: PropTypes.string,
      cancelLabel: PropTypes.string,
    }),
  ]),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  danger: PropTypes.bool,
  className: PropTypes.string,
};

export default ActionButton;
